import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IQBoostViewStore } from '@maximizer/ai/data-access';
import { OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { KENDO_POPUP } from '@progress/kendo-angular-popup';
import { of, switchMap } from 'rxjs';
import { sameKeys } from '../classes/value-checks';
import { AnswerActionsComponent } from '../iqboost-v2/actions/answer-actions.component';
import { CrmCitationCardComponent } from '../iqboost-v2/citations/crm-citation-card.component';
import {
  collectCrmCitationKeys,
  CrmCitation,
  readCrmCitationFromElement,
} from '../iqboost-v2/citations/crm-citation.model';
import { CrmCitationService } from '../iqboost-v2/citations/crm-citation.service';
import {
  AnswerFollowUp,
  AnswerFollowUpsComponent,
} from '../iqboost-v2/follow-ups/answer-follow-ups.component';
import {
  CRM_CITATION_CLASS,
  CrmCitationRefPipe,
} from '../iqboost-v2/pipes/crm-citation-ref.pipe';
import { WebSourceRefPipe } from '../iqboost-v2/pipes/web-source-ref.pipe';
import { EntityKeyRefPipe } from '../pipes/entity-key-ref.pipe';
import { MarkdownRenderingPipe } from '../pipes/markdown-rendering.pipe';
import { createEntityFromReferenceElement } from '../pipes/ref-link.utils';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { WebSearchRefPipe } from '../pipes/web-search-ref.pipe';
import { AnswerCardComponent } from './answer-card.component';
import { AnswerChartComponent } from './answer-chart.component';
import { AnswerChipListComponent } from './answer-chip-list.component';
import { AnswerFactsComponent } from './answer-facts.component';
import { AnswerListComponent } from './answer-list.component';
import { AnswerProfileComponent } from './answer-profile.component';
import { AnswerSourcesComponent } from './answer-sources.component';
import { AnswerStackComponent } from './answer-stack.component';
import { AnswerStatsComponent } from './answer-stats.component';
import { AnswerTableComponent } from './answer-table.component';
import { AnswerTimelineComponent } from './answer-timeline.component';
import { AnswerSegment, parseAnswerSegments } from './answer-segment';
import {
  AnswerFollowUps,
  AnswerSources,
  AnswerWidget,
} from './answer-widget.model';

/** The card of the citation chip the pointer or the focus is on. */
interface CitationCard {
  anchor: HTMLElement;
  citation: CrmCitation;
}

const CARD_CLASS = 'crm-citation-popup';

// The card waits for a pointer that stops on a chip, and survives the gap between chip and card.
const CARD_OPEN_DELAY_MS = 250;
const CARD_CLOSE_DELAY_MS = 200;

// Nothing here de-anonymizes: the `anonymized_mcp` orchestrator puts the real values back on the
// server before the answer arrives.
@Component({
  selector: 'ai-answer-content',
  templateUrl: './answer-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AnswerActionsComponent,
    AnswerCardComponent,
    AnswerChartComponent,
    AnswerChipListComponent,
    AnswerFactsComponent,
    AnswerFollowUpsComponent,
    AnswerListComponent,
    AnswerProfileComponent,
    AnswerSourcesComponent,
    AnswerStackComponent,
    AnswerStatsComponent,
    AnswerTableComponent,
    AnswerTimelineComponent,
    CrmCitationCardComponent,
    CrmCitationRefPipe,
    EntityKeyRefPipe,
    KENDO_POPUP,
    MarkdownRenderingPipe,
    SafeHtmlPipe,
    WebSearchRefPipe,
    WebSourceRefPipe,
  ],
  // The chips are markup a pipe wrote, so their events are read here, where they bubble to.
  host: {
    '(click)': 'openCitationAt($event.target)',
    '(mouseover)': 'pointerEntered($event.target)',
    '(mouseout)': 'pointerLeft($event.target, $event.relatedTarget)',
    '(focusin)': 'focusEntered($event.target)',
    '(focusout)': 'focusLeft($event.relatedTarget)',
    '(keydown.escape)': 'closeCard()',
  },
})
export class AnswerContentComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly store = inject(IQBoostViewStore);
  private readonly citationRecords = inject(CrmCitationService);

  readonly content = input<string | null>();

  // Off while the answer streams, so a half-written citation is not rendered as a chip.
  readonly citations = input(false);

  // Suggested actions create records, so their cards are drawn on a committed answer only.
  readonly actions = input(false);

  // The questions to ask next are drawn under the last answer of a conversation only.
  readonly followUps = input(false);

  // The conversation the answer belongs to, which keeps its cards' state apart from another's.
  readonly threadId = input<string | null>(null);

  readonly streaming = input(false);

  /** The reader's own message: a block in it shows as the code they typed. */
  readonly fromReader = input(false);

  /** A follow-up chip the reader picked, for the chat to ask. */
  readonly followUpAsked = output<AnswerFollowUp>();

  // Each chunk parses fresh objects; a widget handed a new one rebuilds (Kendo's Timeline throws
  // NG0956), so each fence keeps the object it was first parsed into.
  private readonly widgets = new Map<string, AnswerWidget>();

  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly parsed = computed<AnswerSegment[]>(() =>
    parseAnswerSegments(this.content(), { streaming: this.streaming() }),
  );

  protected readonly segments = computed<AnswerSegment[]>(() => {
    const actions = this.actions();

    return this.parsed().flatMap((segment): AnswerSegment[] => {
      if (segment.kind !== 'widget') {
        return [segment];
      }

      if (segment.widget.kind === 'actions' && !actions) {
        return this.hidden(segment);
      }

      // The chips close the answer wherever the model wrote the block: drawn after the segments.
      if (segment.widget.kind === 'followups') {
        return this.followUps() ? [] : this.hidden(segment);
      }

      return [
        {
          ...segment,
          widget: this.stableWidget(segment.source, segment.widget),
        },
      ];
    });
  });

  /** The questions to ask next, where this answer is the one to ask them after. */
  protected readonly followUpBlock = computed<AnswerFollowUps | null>(() => {
    if (!this.followUps()) {
      return null;
    }

    for (const segment of this.parsed()) {
      if (segment.kind === 'widget' && segment.widget.kind === 'followups') {
        return segment.widget;
      }
    }

    return null;
  });

  /**
   * The answer's web sources, which number the markers in its prose. The block closes last, so
   * while an answer streams the markers stay plain text and turn into links once it is whole.
   */
  protected readonly sources = computed<AnswerSources | null>(() => {
    for (const segment of this.segments()) {
      if (segment.kind === 'widget' && segment.widget.kind === 'sources') {
        return segment.widget;
      }
    }

    return null;
  });

  // The records a finished answer cites are read once, together, when the answer is shown.
  private readonly citedKeys = computed(
    () => (this.citations() ? collectCrmCitationKeys(this.content()) : []),
    { equal: sameKeys },
  );

  protected readonly records = toSignal(
    toObservable(this.citedKeys).pipe(
      switchMap((keys) =>
        keys.length ? this.citationRecords.resolve(keys) : of(null),
      ),
    ),
    { initialValue: null },
  );

  protected readonly card = signal<CitationCard | null>(null);

  protected readonly cardRecord = computed(() => {
    const card = this.card();
    return card ? (this.records()?.get(card.citation.key) ?? null) : null;
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearTimers());
  }

  protected openCitationAt(target: EventTarget | null): void {
    const chip = this.chipOf(target);
    if (chip) {
      this.openRecord(chip);
    }
  }

  /** Opens the record a chip cites, as the answer's host decides: a viewer for a PDF, a dialog otherwise. */
  protected openRecord(chip: HTMLElement): void {
    const entity = createEntityFromReferenceElement(chip);
    this.closeCard();
    if (entity) {
      this.store.triggerOpenEntity(new OpenEntityArgs(entity));
    }
  }

  protected pointerEntered(target: EventTarget | null): void {
    const chip = this.chipOf(target);
    if (chip) {
      this.cancelClose();
      if (this.card()?.anchor !== chip) {
        this.scheduleOpen(chip);
      }
      return;
    }

    if (this.insideCard(target)) {
      this.cancelClose();
      return;
    }

    this.cancelOpen();
  }

  protected pointerLeft(
    target: EventTarget | null,
    next: EventTarget | null,
  ): void {
    if (!this.chipOf(target) && !this.insideCard(target)) {
      return;
    }
    // Moving within the chip, or on to the card or another chip, is handled by where it arrives.
    if (this.chipOf(next) || this.insideCard(next)) {
      return;
    }

    this.cancelOpen();
    this.scheduleClose();
  }

  protected focusEntered(target: EventTarget | null): void {
    const chip = this.chipOf(target);
    if (chip) {
      this.cancelClose();
      this.openCard(chip);
    } else if (this.insideCard(target)) {
      this.cancelClose();
    }
  }

  protected focusLeft(next: EventTarget | null): void {
    if (this.chipOf(next) || this.insideCard(next)) {
      return;
    }

    this.closeCard();
  }

  protected closeCard(): void {
    this.clearTimers();
    this.card.set(null);
  }

  private openCard(chip: HTMLElement): void {
    this.cancelOpen();
    const citation = readCrmCitationFromElement(chip);
    this.card.set(citation ? { anchor: chip, citation } : null);
  }

  private scheduleOpen(chip: HTMLElement): void {
    this.cancelOpen();
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.openCard(chip);
    }, CARD_OPEN_DELAY_MS);
  }

  private scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.card.set(null);
    }, CARD_CLOSE_DELAY_MS);
  }

  private cancelOpen(): void {
    if (this.openTimer !== null) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private cancelClose(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private clearTimers(): void {
    this.cancelOpen();
    this.cancelClose();
  }

  // Only this answer's chips: a pointer leaving for another answer's chip must close this card.
  private chipOf(target: EventTarget | null): HTMLElement | null {
    const chip =
      target instanceof Element
        ? target.closest<HTMLElement>(`.${CRM_CITATION_CLASS}`)
        : null;

    return chip && this.host.nativeElement.contains(chip) ? chip : null;
  }

  private insideCard(target: EventTarget | null): boolean {
    const card =
      target instanceof Element ? target.closest(`.${CARD_CLASS}`) : null;

    return !!card && this.host.nativeElement.contains(card);
  }

  // Held back while streaming and dropped from an answer; in the reader's own bubble it shows
  // as the code they typed, so nothing of theirs is hidden and nothing is made from it.
  private hidden(segment: AnswerSegment & { kind: 'widget' }): AnswerSegment[] {
    return this.fromReader()
      ? [{ kind: 'markdown', markdown: segment.source }]
      : [];
  }

  private stableWidget(source: string, widget: AnswerWidget): AnswerWidget {
    const cached = this.widgets.get(source);

    if (cached) {
      return cached;
    }

    this.widgets.set(source, widget);

    return widget;
  }
}
